import { useEffect, useRef, useState, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { IMAGE_API_BASE_URL } from '../../config/api';
import { Button } from '../../components/ui/button';
import { ChevronDown, Menu } from 'lucide-react';
import {
  USER_ROUTES,
  ADMIN_ROUTES,
  IB_ROUTES,
} from '../../constants/routeConstants';

interface HeaderProps {
  logo: string;
  userLabel?: string;
  children?: ReactNode;
  showMobileMenu?: boolean;
  isMobileMenuOpen?: boolean;
  onMobileMenuToggle?: () => void;
}

const Header = ({
  logo,
  userLabel,
  children,
  showMobileMenu,
  isMobileMenuOpen,
  onMobileMenuToggle,
}: HeaderProps) => {
  const { user } = useAuth();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const getLogoLink = () => {
    if (userLabel?.includes('Admin')) {
      return ADMIN_ROUTES.ADMIN_DASHBOARD;
    }
    if (location.pathname.startsWith('/ib')) {
      return IB_ROUTES.USER_IB_DASHBOARD;
    }
    return USER_ROUTES.USER_DASHBOARD;
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getUserDisplay = () => {
    if (userLabel) {
      return userLabel;
    }
    return `${user?.fullName}`.trim();
  };

  const getUserInitial = () => {
    if (userLabel?.includes('Admin')) {
      return user?.fullName?.charAt(0) || 'A';
    }
    return user?.fullName?.charAt(0).toUpperCase() || 'U';
  };

  return (
    <header className="bg-white shadow sticky top-0 z-50">
      <div className="max-w-8xl mx-auto px-2 sm:px-4 lg:px-4">
        <div className="flex justify-between h-12">
          <div className="flex items-center">
            {showMobileMenu && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onMobileMenuToggle}
                className="lg:hidden mr-2"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <img src={logo} alt="Logo" className="h-4 w-auto" />
                ) : (
                  <Menu className="h-4 w-4" />
                )}
              </Button>
            )}
            <Link to={getLogoLink()}>
              <img
                src={logo}
                alt="Logo"
                className="h-8 w-auto cursor-pointer"
              />
            </Link>
          </div>

          <div className="flex items-center space-x-4">
            <div className="relative" ref={dropdownRef}>
              <Button
                variant="ghost"
                onClick={toggleDropdown}
                className="flex items-center space-x-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50 px-2 py-1 h-auto"
              >
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold overflow-hidden">
                  {user?.verification?.facialVerification ? (
                    <img
                      src={`${IMAGE_API_BASE_URL}${user.verification.facialVerification}`}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                      key={user.verification.facialVerification}
                      crossOrigin="anonymous"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    getUserInitial()
                  )}
                </div>
                <span>{getUserDisplay()}</span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-200 ${isDropdownOpen ? 'transform rotate-180' : ''}`}
                />
              </Button>
              {isDropdownOpen && (
                <div
                  className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50"
                  onClick={() => setIsDropdownOpen(false)}
                >
                  {children}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
